import {IntlShape} from "react-intl";

export type Route = {
    name: (intl: IntlShape) => string,
    relativePath: string,
    absolutePath: (...param: number[]) => string,
    precededBy?: Route
}

type RouteName =
    | "index"
    | "appLoader"
    | "employeeEnvironment"
    | "auth"
    | "signIn"
    | "reset"
    | "confirmReset"
    | "adminHome"
    | "account"
    | "systemInfo"
    | "location"
    | "deliveryAreas"
    | "employees"
    | "newEmployee"
    | "reports"
    | "timesheet"
    | "dayReport"
    | "cartTimeline"
    | "featuredProducts"
    | "menu"
    | "optionGroups"
    | "newFeaturedProduct"
    | "busynessManagement"
    | "loyaltyRewards"
    | "newLoyaltyReward"
    | "jobOpenings"
    | "jobApplications"
    | "posAdminDashboardHome"
    | "posDashboardHome";

type Router = {
    [routes in RouteName]: Route
}

// Base:
const index: Route = {
    name: intl => intl.formatMessage({id: "appName"}),
    relativePath: "/",
    absolutePath: () => "/",
};

const appLoader: Route = {
    name: intl => intl.formatMessage({id: "loading-title"}),
    relativePath: "/",
    absolutePath: () => "/",
};

// Employee environment
const employeeEnvironmentPart = "employee";
const employeeEnvironment: Route = {
    name: intl => intl.formatMessage({id: "employee"}),
    relativePath: employeeEnvironmentPart,
    absolutePath: () => `${index.absolutePath()}${employeeEnvironmentPart}`,
    precededBy: index,
};

// Unauthenticated:
const authPart = "auth";
const auth: Route = {
    name: intl => intl.formatMessage({id: "dashboard"}),
    relativePath: authPart,
    absolutePath: () => `${index.absolutePath()}${authPart}`
};

const signInPart = "sign-in";
const signIn: Route = {
    name: intl => intl.formatMessage({id: "signIn"}),
    relativePath: signInPart,
    absolutePath: () => `${auth.absolutePath()}/${signInPart}`
};

const resetPart = "reset";
const reset: Route = {
    name: intl => intl.formatMessage({id: "resetPassword"}),
    relativePath: resetPart,
    absolutePath: () => `${auth.absolutePath()}/${resetPart}`
};

const confirmResetPart = "confirm";
const confirmReset: Route = {
    name: intl => intl.formatMessage({id: "setPassword"}),
    relativePath: confirmResetPart,
    absolutePath: () => `${reset.absolutePath()}/${confirmResetPart}`
};

// Authenticated admin:
const adminHomePart = "admin";
const adminHome: Route = {
    name: intl => intl.formatMessage({id: "dashboard"}),
    relativePath: adminHomePart,
    absolutePath: () => `${index.absolutePath()}${adminHomePart}`
};

const systemInfoPart = "systeem";
const systemInfo: Route = {
    name: intl => intl.formatMessage({id: "system"}),
    relativePath: systemInfoPart,
    absolutePath: () => `${adminHome.absolutePath()}/${systemInfoPart}`,
    precededBy: adminHome,
};

const accountPart = "account";
const account: Route = {
    name: intl => intl.formatMessage({id: "account"}),
    relativePath: accountPart,
    absolutePath: () => `${adminHome.absolutePath()}/${accountPart}`,
    precededBy: adminHome,
};

const locationPart = "location";
const location: Route = {
    name: intl => intl.formatMessage({id: "locationDetails"}),
    relativePath: locationPart,
    absolutePath: () => `${adminHome.absolutePath()}/${locationPart}`,
    precededBy: adminHome,
};

const deliveryAreasPart = "delivery-areas";
const deliveryAreas: Route = {
    name: intl => intl.formatMessage({id: "deliveryAreas"}),
    relativePath: deliveryAreasPart,
    absolutePath: () => `${adminHome.absolutePath()}/${deliveryAreasPart}`,
    precededBy: adminHome,
};

const employeesPart = "employees";
const employees: Route = {
    name: intl => intl.formatMessage({id: "employees"}),
    relativePath: employeesPart,
    absolutePath: () => `${adminHome.absolutePath()}/${employeesPart}`,
    precededBy: adminHome,
};

const editEmployeePart = ":employeeId";
export const editEmployee = (employeeId?: number,): Route => ({
    name: intl => intl.formatMessage({id: "employeeWithId"}, {employeeId: employeeId || -1}),
    relativePath: editEmployeePart,
    absolutePath: () => `${employees.absolutePath()}/${employeeId}`,
    precededBy: employees,
});

export const newEmployeePart = "new";
const newEmployee: Route = {
    name: intl => intl.formatMessage({id: "newEmployee"}),
    relativePath: newEmployeePart,
    absolutePath: () => `${employees.absolutePath()}/${newEmployeePart}`,
    precededBy: employees,
};

const reportsPart = "reports";
const reports: Route = {
    name: intl => intl.formatMessage({id: "reports"}),
    relativePath: reportsPart,
    absolutePath: () => `${adminHome.absolutePath()}/${reportsPart}`,
    precededBy: adminHome,
};

const timesheetPart = "timesheet";
const timesheet: Route = {
    name: intl => intl.formatMessage({id: "timesheet"}),
    relativePath: timesheetPart,
    absolutePath: () => `${reports.absolutePath()}/${timesheetPart}`,
    precededBy: reports,
};

const dayReportPart = "day-report";
const dayReport: Route = {
    name: intl => intl.formatMessage({id: "dayReport"}),
    relativePath: dayReportPart,
    absolutePath: () => `${reports.absolutePath()}/${dayReportPart}`,
    precededBy: reports,
};

const cartTimelinePart = "carts";
const cartTimeline: Route = {
    name: intl => intl.formatMessage({id: "cartTimeline"}),
    relativePath: cartTimelinePart,
    absolutePath: () => `${reports.absolutePath()}/${cartTimelinePart}`,
    precededBy: reports,
};

const menuPart = "menu";
const menu: Route = {
    name: intl => intl.formatMessage({id: "menu"}),
    relativePath: menuPart,
    absolutePath: () => `${adminHome.absolutePath()}/${menuPart}`,
    precededBy: adminHome,
};

const optionGroupsPart = "option-groups";
const optionGroups: Route = {
    name: intl => intl.formatMessage({id: "optionGroups"}),
    relativePath: optionGroupsPart,
    absolutePath: () => `${menu.absolutePath()}/${optionGroupsPart}`,
    precededBy: menu,
};

const featuredProductsPart = "featured-products";
const featuredProducts: Route = {
    name: intl => intl.formatMessage({id: "featuredProducts"}),
    relativePath: featuredProductsPart,
    absolutePath: () => `${adminHome.absolutePath()}/${featuredProductsPart}`,
    precededBy: adminHome,
};

const editFeaturedProductPart = ":featuredProductId";
export const editFeaturedProduct = (featuredProductId?: number,): Route => ({
    name: intl => intl.formatMessage({id: "featuredProductWithId"}, {featuredProductId: featuredProductId || -1}),
    relativePath: editFeaturedProductPart,
    absolutePath: () => `${featuredProducts.absolutePath()}/${featuredProductId}`,
    precededBy: featuredProducts,
});

export const newFeaturedProductPart = "new";
const newFeaturedProduct: Route = {
    name: intl => intl.formatMessage({id: "newFeaturedProduct"}),
    relativePath: newFeaturedProductPart,
    absolutePath: () => `${featuredProducts.absolutePath()}/${newFeaturedProductPart}`,
    precededBy: featuredProducts,
};

const loyaltyRewardsPart = "loyalty-rewards";
const loyaltyRewards: Route = {
    name: intl => intl.formatMessage({id: "loyaltyRewards"}),
    relativePath: loyaltyRewardsPart,
    absolutePath: () => `${adminHome.absolutePath()}/${loyaltyRewardsPart}`,
    precededBy: adminHome,
};

const editLoyaltyRewardPart = ":loyaltyRewardId";
export const editLoyaltyReward = (loyaltyRewardId?: number,): Route => ({
    name: intl => intl.formatMessage({id: "loyaltyRewardWithId"}, {id: loyaltyRewardId || -1}),
    relativePath: editLoyaltyRewardPart,
    absolutePath: () => `${loyaltyRewards.absolutePath()}/${loyaltyRewardId}`,
    precededBy: loyaltyRewards,
});

export const newLoyaltyRewardPart = "new";
const newLoyaltyReward: Route = {
    name: intl => intl.formatMessage({id: "newLoyaltyReward"}),
    relativePath: newLoyaltyRewardPart,
    absolutePath: () => `${loyaltyRewards.absolutePath()}/${newLoyaltyRewardPart}`,
    precededBy: loyaltyRewards,
};

const busynessManagementPart = "busyness-management";
const busynessManagement: Route = {
    name: intl => intl.formatMessage({id: "busynessManagement"}),
    relativePath: busynessManagementPart,
    absolutePath: () => `${adminHome.absolutePath()}/${busynessManagementPart}`,
    precededBy: adminHome,
};

const jobOpeningsPart = "job-openings";
const jobOpenings: Route = {
    name: intl => intl.formatMessage({id: "jobOpenings"}),
    relativePath: jobOpeningsPart,
    absolutePath: () => `${adminHome.absolutePath()}/${jobOpeningsPart}`,
    precededBy: adminHome,
};

const jobApplicationsPart = "job-applications";
const jobApplications: Route = {
    name: intl => intl.formatMessage({id: "jobApplications"}),
    relativePath: jobApplicationsPart,
    absolutePath: () => `${adminHome.absolutePath()}/${jobApplicationsPart}`,
    precededBy: adminHome,
};

export const editJobApplicationPart = ":jobApplicationId";
export const editJobApplication = (jobApplicationId?: number,): Route => ({
    name: intl => intl.formatMessage({id: "jobApplication"}),
    relativePath: editJobApplicationPart,
    absolutePath: () => `${jobApplications.absolutePath()}/${jobApplicationId}`,
    precededBy: jobApplications,
});

// Authenticated POS:
const posAdminDashboardHomePart = "pos";
const posAdminDashboardHome: Route = {
    name: intl => intl.formatMessage({id: "dashboard"}),
    relativePath: posAdminDashboardHomePart,
    absolutePath: () => `${index.absolutePath()}${posAdminDashboardHomePart}`
};

const posDashboardHomePart = "dashboard";
const posDashboardHome: Route = {
    name: intl => intl.formatMessage({id: "pos"}),
    relativePath: posDashboardHomePart,
    absolutePath: () => `${posAdminDashboardHome.absolutePath()}/${posDashboardHomePart}`,
    precededBy: posAdminDashboardHome,
};

export const posOrdersPart = "orders";
export const posOrderPart = ":publicCartId?";
export const posOrders = (publicCartId?: string,): Route => ({
    name: intl => intl.formatMessage({id: "pos"}),
    relativePath: posOrdersPart,
    absolutePath: () => publicCartId === undefined
        ? `${posDashboardHome.absolutePath()}/${posOrdersPart}`
        : `${posDashboardHome.absolutePath()}/${posOrdersPart}/${publicCartId}`,
    precededBy: posDashboardHome,
});

export const posUpcomingOrdersPart = "upcoming";
export const posUpcomingOrders: Route = {
    name: intl => intl.formatMessage({id: "pos"}),
    relativePath: posUpcomingOrdersPart,
    absolutePath: () => `${posDashboardHome.absolutePath()}/${posUpcomingOrdersPart}`,
    precededBy: posDashboardHome,
};

export const posEditOrderPart = "edit-order";
export const posEditOrder = (publicCartId?: string,): Route => ({
    name: intl => intl.formatMessage({id: "pos"}),
    relativePath: posEditOrderPart,
    absolutePath: () => publicCartId === undefined
        ? `${posDashboardHome.absolutePath()}/${posEditOrderPart}`
        : `${posDashboardHome.absolutePath()}/${posEditOrderPart}/${publicCartId}`,
    precededBy: posDashboardHome,
});

// /pos
// /pos/dashboard
// /pos/dashboard/orders
// /pos/dashboard/orders/:order-id
// /pos/dashboard/edit-order/
// /pos/dashboard/edit-order/:order-id?
// /pos/dashboard/upcoming

const router: Router = {
    // General:
    index,
    appLoader,

    // Auth:
    auth,
    signIn,
    reset,
    confirmReset,

    // Admin:
    adminHome,
    account,
    systemInfo,
    location,
    deliveryAreas,
    employees,
    newEmployee,
    reports,
    timesheet,
    dayReport,
    cartTimeline,
    featuredProducts,
    newFeaturedProduct,
    busynessManagement,
    loyaltyRewards,
    newLoyaltyReward,
    menu,
    optionGroups,
    jobOpenings,
    jobApplications,

    // POS;
    posAdminDashboardHome,
    posDashboardHome,

    // Employees (clock system):
    employeeEnvironment,
};

export default router;